import React from 'react';

export default function AddOnInfo({ subscription }) {
  const { spam_shield } = subscription;
  const usage              = spam_shield?.usage || 0;
  const freeLimit          = spam_shield?.free_verification_limit || 50;
  const freeUsed           = spam_shield?.free_used || 0;
  const billable           = spam_shield?.billable || 0;
  const flatFee            = 15.00;
  const meteredRate        = 0.01;
  const verificationCharge = parseFloat((billable * meteredRate).toFixed(2));

  const isActive     = spam_shield?.enabled;
  const isCancelling = spam_shield?.cancelling;  // disabled but subscription still alive until period end
  const isManaged    = spam_shield?.managed;     // enterprise/managed — billed manually, no Stripe charges shown

  return (
    <div className="AddOnInfo">
      <h5 className="page-header">Active Add-ons</h5>
      <div className="billing-section-content">

        {(isActive || isCancelling) ? (
          <div className="add-on-card">

            <div className="card-header">
              <div className="add-on-name">Spam Shield Protection</div>
              <div className="status-badge" style={isCancelling ? { backgroundColor: '#fef3c7', color: '#92400e' } : {}}>
                {isCancelling ? 'Cancels at period end' : (spam_shield.status === 'active' ? 'Active' : spam_shield.status || 'Active')}
              </div>
            </div>

            {/* Cancelling notice */}
            {isCancelling && (
              <div style={{ fontSize: '13px', color: '#92400e', marginBottom: '12px', fontStyle: 'italic' }}>
                Spam Shield has been disabled. Protection stopped immediately. Any pending email verification charges below will be collected on <strong>{spam_shield.renew_on || 'the billing cycle end date'}</strong>, then the subscription will cancel automatically.
              </div>
            )}

            {/* Renewal / cancellation date */}
            {spam_shield.renew_on && !isCancelling && (
              <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '12px' }}>
                Current billing cycle ends <strong>{spam_shield.renew_on}</strong>
              </div>
            )}

            <div className="cost-breakdown">
              {/* Base fee — always show when active */}
              {isActive && (
                <div className="cost-item">
                  <span className="label">Monthly Base Protection</span>
                  <span className="value">${flatFee.toFixed(2)}/mo</span>
                </div>
              )}

              {/* Verification enabled and NOT cancelling — show free quota + billable breakdown */}
              {!isCancelling && spam_shield.verification_enabled && (
                <div className="cost-item">
                  <span className="label">
                    Email Verification (this cycle) — {usage} {usage === 1 ? 'email' : 'emails'}
                    {' '}
                    {freeLimit > 0 ? (
                      <div style={{ color: '#16a34a', fontSize: '12px', marginTop: '3px' }}>
                        ({freeUsed} of {freeLimit} free used
                        {billable > 0 ? `, ${billable} charged × $${meteredRate.toFixed(2)}` : ', no extra charge'})
                      </div>
                    ) : (
                      <div style={{ fontSize: '12px', color: '#64748b', marginTop: '3px' }}>
                        × ${meteredRate.toFixed(2)} each
                      </div>
                    )}
                  </span>
                  <span className="value">${verificationCharge.toFixed(2)}</span>
                </div>
              )}

              {/* Cancelling or verification disabled mid-cycle — show pending billable amount still owed */}
              {(isCancelling || !spam_shield.verification_enabled) && billable > 0 && (
                <div className="cost-item" style={{ color: '#92400e' }}>
                  <span className="label">
                    Email Verification (pending) — {billable} charged × ${meteredRate.toFixed(2)}
                    {' '}
                    {freeLimit > 0 && (
                      <span style={{ fontSize: '12px' }}>({freeUsed} of {freeLimit} free already used)</span>
                    )}
                  </span>
                  <span className="value">${verificationCharge.toFixed(2)}</span>
                </div>
              )}
            </div>

            {/* Estimated total — show for all active users */}
            {isActive && (spam_shield.verification_enabled || billable > 0) && (
              <div className="total-estimation">
                <div className="total-label">
                  {isManaged
                    ? 'Estimated charge this month'
                    : `Estimated charge on ${spam_shield.renew_on || 'next billing date'}`}
                </div>
                <div className="total-value">
                  ${(flatFee + verificationCharge).toFixed(2)}
                </div>
              </div>
            )}

            {/* Pending total — cancelling with outstanding verification charges */}
            {isCancelling && usage > 0 && (
              <div className="total-estimation" style={{ borderColor: '#f59e0b' }}>
                <div className="total-label">
                  Pending charge on {spam_shield.renew_on || 'cycle end date'}
                </div>
                <div className="total-value">${verificationCharge.toFixed(2)}</div>
              </div>
            )}

            {!isManaged && (
              <div style={{ marginTop: '15px', fontSize: '12px', color: '#888', fontStyle: 'italic' }}>
                * Spam Shield runs on its own monthly cycle, independent of your main plan.
                Verification usage shown is for the current billing cycle only and matches your invoice exactly.
              </div>
            )}

          </div>
        ) : (
          <div className="add-on-section">
            <div style={{ color: '#666', fontSize: '0.9em', fontStyle: 'italic' }}>
              No active add-ons. Enable Spam Shield in Site Settings to get started.
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
